import React, { useState, useEffect, useMemo } from 'react';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Calendar } from 'primereact/calendar';
import '../css/Pages.css';

const Dashboard = () => {
    const [isLoading, setIsLoading] = useState(true);

    const [rawData, setRawData] = useState({
        articles: [], topics: [], books: [], others: [], users: []
    });

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const authHeaders = useMemo(() => ({
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json'
    }), []);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setIsLoading(true);
        const baseUrl = process.env.REACT_APP_API_BASE_URL;

        try {
            const [resArt, resTopic, resBook, resOther, resUser] = await Promise.all([
                fetch(`${baseUrl}/scientificarticlemngt?userId=${currentUser.Id}&isAdmin=true`, { headers: authHeaders }),
                fetch(`${baseUrl}/scientifictopic?userId=${currentUser.Id}&isAdmin=true`, { headers: authHeaders }),
                fetch(`${baseUrl}/bookpublication?userId=${currentUser.Id}&isAdmin=true`, { headers: authHeaders }),
                fetch(`${baseUrl}/othersciencedeclaration?userId=${currentUser.Id}&isAdmin=true`, { headers: authHeaders }),
                fetch(`${baseUrl}/user`, { headers: authHeaders })
            ]);

            const articles = await resArt.json().catch(() => []);
            const topics = await resTopic.json().catch(() => []);
            const books = await resBook.json().catch(() => []);
            const others = await resOther.json().catch(() => []);
            const users = await resUser.json().catch(() => []);

            setRawData({
                articles: Array.isArray(articles) ? articles : [],
                topics: Array.isArray(topics) ? topics : [],
                books: Array.isArray(books) ? books : [],
                others: Array.isArray(others) ? others : [],
                users: Array.isArray(users) ? users : []
            });

        } catch (error) {
            console.error("Lỗi tải dashboard:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        const filterFn = (item) => {
            if (!startDate && !endDate) return true;

            const dateStr = item.PublicationDate || item.AcceptanceDate || item.StartDate || item.ApplyDate;
            if (!dateStr) return false;

            const d = new Date(dateStr);
            d.setHours(0, 0, 0, 0);

            if (startDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                if (d < start) return false;
            }

            if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                if (d > end) return false;
            }

            return true;
        };

        return {
            articles: rawData.articles.filter(filterFn),
            topics: rawData.topics.filter(filterFn),
            books: rawData.books.filter(filterFn),
            others: rawData.others.filter(filterFn)
        };
    }, [rawData, startDate, endDate]);

    const dataCounts = useMemo(() => {
        const processStatus = (list) => {
            return {
                keKhai: list.filter(item => item.Status === 'Lưu nháp' || item.Status === 'Đã gửi duyệt').length,
                daDuyet: list.filter(item => item.Status === 'Đã duyệt').length
            };
        };
        return {
            articles: processStatus(filteredData.articles),
            topics: processStatus(filteredData.topics),
            books: processStatus(filteredData.books),
            others: processStatus(filteredData.others)
        };
    }, [filteredData]);

    const topAuthors = useMemo(() => {
        const authorMap = {};
        filteredData.articles.filter(a => a.Status === 'Đã duyệt').forEach(art => {
            const name = art.FullName || art.CreatedByEmail || "Ẩn danh";
            const department = art.DepartmentName || "Đơn vị khác";

            if (!authorMap[name]) {
                authorMap[name] = { count: 0, department: department };
            }
            authorMap[name].count += 1;
        });

        return Object.entries(authorMap)
            .map(([name, data]) => ({ name, count: data.count, department: data.department }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [filteredData.articles]);

    const yearlyData = useMemo(() => {
        const years = {};
        [...rawData.articles, ...rawData.topics, ...rawData.books].filter(i => i.Status === 'Đã duyệt').forEach(item => {
            const dateStr = item.PublicationDate || item.AcceptanceDate || item.StartDate;
            if (dateStr) {
                const year = new Date(dateStr).getFullYear();
                years[year] = (years[year] || 0) + 1;
            }
        });
        return years;
    }, [rawData]);

    const hrData = useMemo(() => {
        const deptMap = {};
        rawData.users.forEach(u => {
            if (u.DepartmentName) {
                deptMap[u.DepartmentName] = (deptMap[u.DepartmentName] || 0) + 1;
            }
        });
        return Object.entries(deptMap).map(([name, val]) => ({ name, val }));
    }, [rawData.users]);

    const barData = {
        labels: ['Bài báo', 'Đề tài', 'Sách - Giáo trình', 'Nội dung NCKH khác'],
        datasets: [
            { label: 'Kê khai', backgroundColor: '#3b82f6', data: [dataCounts.articles.keKhai, dataCounts.topics.keKhai, dataCounts.books.keKhai, dataCounts.others.keKhai] },
            { label: 'Đã duyệt', backgroundColor: '#f59e0b', data: [dataCounts.articles.daDuyet, dataCounts.topics.daDuyet, dataCounts.books.daDuyet, dataCounts.others.daDuyet] }
        ]
    };

    const areaData = {
        labels: Object.keys(yearlyData).sort(),
        datasets: [{
            label: 'Công bố khoa học',
            data: Object.keys(yearlyData).sort().map(y => yearlyData[y]),
            fill: true,
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            tension: 0.4
        }]
    };

    const hrChartData = {
        labels: hrData.map(d => d.name),
        datasets: [{ label: 'Nhân sự', data: hrData.map(d => d.val), backgroundColor: '#a855f7' }]
    };

    const commonOptions = { maintainAspectRatio: false, responsive: true };
    const barOptions = { ...commonOptions, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } };
    const hrOptions = { ...commonOptions, indexAxis: 'y', plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { precision: 0 } } } };

    if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', marginTop: '100px' }}><ProgressSpinner /></div>;

    const styles = {
        container: { padding: '20px', backgroundColor: '#f4f7f6', minHeight: '100vh', boxSizing: 'border-box' },
        header: { backgroundColor: '#1e3a8a', backgroundImage: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)', padding: '20px', borderRadius: '10px', color: '#fff', marginBottom: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
        row: { display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' },
        card: { flex: '1 1 45%', backgroundColor: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', minWidth: '300px', display: 'flex', flexDirection: 'column' },
        cardTitleContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' },
        cardTitleText: { margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#444', textTransform: 'uppercase' },
        dateFilterBox: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
        chartWrapper: { flex: 1, minHeight: '300px', position: 'relative' },
        authorItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0' },
        avatarBox: { display: 'flex', alignItems: 'center', gap: '15px' },
        avatar: { width: '45px', height: '45px', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px' },
        badge: { width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #22c55e', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f0fdf4' }
    };

    const renderDateFilter = () => (
        <div style={styles.dateFilterBox}>
            <Calendar
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                placeholder="Từ ngày"
                dateFormat="dd/mm/yy"
                showIcon
                showClear
                style={{ width: '150px' }}
                inputStyle={{ padding: '8px', fontSize: '13px' }}
            />
            <span style={{ color: '#888', fontWeight: 'bold' }}>-</span>
            <Calendar
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                placeholder="Đến ngày"
                dateFormat="dd/mm/yy"
                showIcon
                showClear
                style={{ width: '150px' }}
                inputStyle={{ padding: '8px', fontSize: '13px' }}
                minDate={startDate || undefined}
            />
        </div>
    );

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={{ margin: 0, textTransform: 'uppercase', fontSize: '22px' }}>Thông tin tổng quan</h2>
                <p style={{ margin: '5px 0 0 0', opacity: 0.8, fontSize: '14px' }}>Hệ thống quản lý khoa học © 2026 - SCV</p>
            </div>

            <div style={styles.row}>
                <div style={{ ...styles.card, flex: '1.5 1 55%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Số lượng công trình</h3>
                        {renderDateFilter()}
                    </div>
                    <div style={styles.chartWrapper}>
                        <Chart type="bar" data={barData} options={barOptions} style={{ height: '100%', width: '100%' }} />
                    </div>
                </div>

                <div style={{ ...styles.card, flex: '1 1 40%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Top 5 công bố bài báo</h3>
                        {renderDateFilter()}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        {topAuthors.length > 0 ? topAuthors.map((author, index) => (
                            <div key={index} style={styles.authorItem}>
                                <div style={styles.avatarBox}>
                                    <div style={{ ...styles.avatar, backgroundColor: ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444'][index % 5] }}>
                                        {author.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 'bold', color: '#333', fontSize: '15px' }}>{author.name}</div>
                                        <div style={{ fontSize: '13px', color: '#888', marginTop: '3px' }}>{author.department}</div>
                                    </div>
                                </div>
                                <div style={styles.badge}>{author.count}</div>
                            </div>
                        )) : (
                            <p style={{ textAlign: 'center', color: '#888', marginTop: '20px' }}>Không có dữ liệu trong khoảng thời gian này</p>
                        )}
                    </div>
                </div>
            </div>

            <div style={styles.row}>
                <div style={{ ...styles.card, flex: '1 1 100%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Số lượng công bố khoa học qua các năm</h3>
                    </div>
                    <div style={{ height: '350px', position: 'relative' }}>
                        <Chart type="line" data={areaData} options={barOptions} style={{ height: '100%', width: '100%' }} />
                    </div>
                </div>
            </div>

            <div style={styles.row}>
                <div style={{ ...styles.card, flex: '1 1 100%' }}>
                    <div style={styles.cardTitleContainer}>
                        <h3 style={styles.cardTitleText}>Thống kê nhân sự theo đơn vị</h3>
                    </div>
                    <div style={{ height: '400px', position: 'relative' }}>
                        <Chart type="bar" data={hrChartData} options={hrOptions} style={{ height: '100%', width: '100%' }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;